import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { FunctionId } from '@/types/function-id';
import { getLlmModelHeader, getLlmModelParam } from '@/utils/agentic-search/llm-model';

/**
 * TRIZ 原理项
 */
export interface TrizPrincipleItem {
    id: number;
    name: string;
    description: string[];
}

/**
 * TRIZ 原理列表响应
 */
export interface TrizPrincipleListResponse {
    principles: TrizPrincipleItem[];
    total: number;
}

export interface TrizPrincipleDomainPatent {
    domain: string;
    patent_id: string;
    pn: string;
    title: string;
    abstract: string;
    problem_summary: string;
    effect_summary: string;
    innovation_summary: string;
    how_to_solve: string[];
    triz_technical_contradiction?: TrizPrincipleTechnicalContradiction[];
}

export interface TrizPrincipleFeature {
    parameter_id?: string;
    parameter_name?: string;
    specific_parameter?: string;
}

export interface TrizPrincipleAppliedPrinciple {
    how_to_solve?: string;
    principle_id?: string;
    principle_name?: string;
}

export interface TrizPrincipleTechnicalContradiction {
    contradiction_statement?: string;
    improving_feature?: TrizPrincipleFeature;
    worsening_feature?: TrizPrincipleFeature;
    invention_principles_applied?: TrizPrincipleAppliedPrinciple[];
}

export interface TrizPrincipleScenario {
    title: string;
    description: string;
}

export interface TrizPrincipleDetailResponse {
    id: number;
    name: string;
    description: string[];
    summary: string;
    example: string;
    scenarios: TrizPrincipleScenario[];
    domain_patents: TrizPrincipleDomainPatent[];
    principle_query: string;
}

export interface TrizPrincipleSearchPreviewResponse {
    qid: string;
    playbook: string;
}

export interface DeepCostCertificationContext {
    foodGrade?: boolean;
    rohsReach?: boolean;
    explosionProof?: boolean;
    emcEmi?: boolean;
    automotiveGrade?: boolean;
    biocompatible?: boolean;
    halogenFree?: boolean;
    flameRetardant?: boolean;
    rfCertification?: boolean;
    aviationGrade?: boolean;
    waterPermit?: boolean;
    pfasFree?: boolean;
    pressureVessel?: boolean;
    implantableGrade?: boolean;
    shockproof?: boolean;
    otherCertification?: string;
}

export interface DeepCostWorkingConditionsContext {
    minTemperature?: number;
    maxTemperature?: number;
    saltFog?: string;
    chemicalSolvent?: string;
    sterilization?: string;
    ipRating?: string;
    ikRating?: string;
    loadVibration?: string;
    voltageLimit?: string;
    specialEnvironmentUv?: boolean;
    specialEnvironmentRadiation?: boolean;
    otherWorkingCondition?: string;
}

export interface DeepCostProjectContext {
    projectName?: string;
    industry?: string;
    marketPosition?: string;
    baselineCost?: number;
    productCost?: number;
    productCostUnit?: string;
    targetReduction?: number;
    annualVolume?: number;
    annualVolumeUnit?: string;
    lifecycle?: string;
    productDescription?: string;
    physicalScale?: string;
    certifications?: DeepCostCertificationContext;
    workingConditions?: DeepCostWorkingConditionsContext;
}

/**
 * 创建 TRIZ / DeepCost 任务的参数
 */
export interface CreateTrizJobParams {
    /** 用户输入的问题/描述 */
    user_input?: string;
    /** 已上传文件的 record_id 列表（与上传接口返回一致） */
    record_ids?: string[];
    /** DeepCost 项目结构化上下文 */
    project_context?: DeepCostProjectContext;
}

/**
 * 创建 Tech Research 任务的参数
 */
export interface createTechResearchBetaJobParams {
    agent_ids?: [number];
    input?: string;
    team_ids?:[]
}

/**
 * 创建 FMEA 任务的参数
 */
export interface CreateFmeaJobParams {
    content: string;
    /** 上传文件的 record_id 列表，后端按此取文件解析 */
    record_ids?: string[];
}

/**
 * 创建 Competitive Landscape 任务的参数
 */
export interface CreateCompetitiveLandscapeJobParams {
    message?: string;
}

/**
 * Company Scout 创建任务的参数
 */
export interface CreateCompanyScoutJobParams {
    user_query: string;
}

/**
 * Company Scout 创建任务的响应
 */
export interface CreateCompanyScoutJobResponse {
    company_scout_id: string;
}

/**
 * S3 签名接口请求参数（/eureka/s3/cdn/sign）
 */
export interface GetS3SignUrlsRequest {
    /** object_key 列表，如 /triz/upload/2026-03-04/xxx.png */
    object_keys: string[];
    /** 签名过期时间（秒），如 1800 */
    expire: number;
}

/**
 * S3 签名接口返回的单条 signed_url 项
 */
export interface S3SignedUrlItem {
    /** 与请求的 object_key 对应，如 /triz/upload/2026-03-04/xxx.png */
    sub_resource: string;
    /** 带签名的可访问 URL */
    url: string;
}

/**
 * S3 签名接口响应 data（/eureka/s3/cdn/sign）
 */
export interface GetS3SignUrlsResponse {
    /** 资源域名，如 qa-eureka-work.zhihuiya.com */
    resource: string;
    /** 签名 URL 列表，与请求的 object_keys 一一对应 */
    signed_urls: S3SignedUrlItem[];
}

/**
 * 生成搜索查询 ID 的请求参数
 */
export interface GenerateSearchQueryIdRequest {
    advanced_search: boolean;
    data_type: string;
    query: {
        type: string;
        condition: string;
        must: Array<{
            type: string;
            condition: string;
            any: Array<{
                type: string;
                value: string[];
                condition: string;
            }>;
        }>;
    };
    query_layout: Array<{
        type: string;
        condition: string;
        value: Array<{
            type: string;
            display_name: string;
        }>;
    }>;
}

/**
 * 生成搜索查询 ID 的响应
 */
export interface GenerateSearchQueryIdResponse {
    id: string;
}

/**
 * 获取公司搜索历史记录的请求参数
 */
export interface GetCompanySearchHistoryRequest {
    query: string;
}

/**
 * 公司搜索历史记录项
 */
export interface CompanySearchHistoryItem {
    id: string;
    business_id: string;
    title: string;
}

/**
 * 获取公司搜索历史记录的响应
 */
export type GetCompanySearchHistoryResponse = CompanySearchHistoryItem[];

/**
 * 联想建议项
 */
export interface InputKeywordItem {
    id?: string;
    type?: string;
    value: string;
}

/**
 * 联想建议响应
 */
export interface AutoCompleteSuggestion {
    section: string;
    content: InputKeywordItem[];
}

export interface BiTrackParams {
    event_type: string;
    source_type?: string;
    session_id?: string;
    other_param?: string;
    event_detail_str?: string;
    module_type: string;
}

/**
 * 项目列表项（work project）
 */
export interface WorkProjectItem {
    work_project_id: string;
    title: string;
    [key: string]: unknown;
}

export interface GlobalControl {
    upload: boolean;
    [key: string]: unknown;
}

/**
 * Agentic Search API 服务接口
 */
export interface IAgenticSearchApi {
    /**
     * 创建 TRIZ / DeepCost 任务
     */
    createTrizJob(params: CreateTrizJobParams, agentType?: string): Promise<{ job_id: string }>;

    /**
     * 创建 Tech Research 任务
     */
    createTechResearchBetaJob(params: createTechResearchBetaJobParams, agentType?: string): Promise<{ job_id: string }>;

    /**
     * 创建 FMEA 任务
     */
    createFmeaJob(params: CreateFmeaJobParams, agentType?: string): Promise<{ fmea_id: string }>;

    /**
     * 从上传的 Excel 创建 FMEA(multipart 直传,后端 POI 解析,跳过意图/50字下限校验)
     */
    createFmeaFromExcel(file: File, content?: string, agentType?: string): Promise<{ fmea_id: string }>;

    /**
     * 创建 Competitive Landscape 任务
     */
    createCompetitiveLandscapeJob(params: CreateCompetitiveLandscapeJobParams, agentType?: string): Promise<{ job_id: string }>;

    /**
     * 创建 Company Scout 任务（AI 对话式搜索）
     */
    createCompanyScoutJob(params: CreateCompanyScoutJobParams, agentType?: string): Promise<CreateCompanyScoutJobResponse>;

    /**
     * 生成搜索查询 ID（传统搜索）
     */
    generateSearchQueryId(params: GenerateSearchQueryIdRequest): Promise<GenerateSearchQueryIdResponse>;

    /**
     * 获取公司搜索历史记录
     */
    getCompanySearchHistory(params: GetCompanySearchHistoryRequest): Promise<GetCompanySearchHistoryResponse>;

    /**
     * 获取智能联想建议（统一接口 /eureka/smart/suggest）
     * 返回数据包含多个 section（COMPANY、AUTOCOMPLETE 等）
     * Company Search 使用 COMPANY section
     * Competitive Landscape 使用 AUTOCOMPLETE section
     */
    getSmartSuggestions(params: { keyword: string, fields: string }): Promise<AutoCompleteSuggestion[]>;

    /**
     * 获取 RD 功能卡片列表（用于权限判断）
     */
    getRdCards(): Promise<{ cards: RdCard[] }>;

    /**
     * Quick Chat 任务（AI Agent 平台）
     */
    quickChatJob(params: { code: string; input: string }, agentType?: string): Promise<{ job_id: string }>;

    /**
     * TRIZ / DeepCost 批量上传文件（图片 .jpg/.jpeg/.png/.gif/.webp/.bmp/.tiff/.tif，表格 .xlsx/.xls）
     */
    uploadFileBatch(files: File[], agentType?: string): Promise<PatSnapCommonResponseData<unknown>>;

    /**
     * TRIZ / DeepCost 删除已上传文件（按 record_ids）
     */
    deleteUploadFileRecords(recordIds: string[], agentType?: string): Promise<PatSnapCommonResponseData<unknown>>;

    /**
     * 获取 S3 签名 CDN 地址（用于图片等预览）
     * @param objectKeys object_key 列表
     * @param expire 过期时间（秒），默认 1800
     * @returns 接口原始 data：resource + signed_urls[]
     */
    getS3SignUrls(objectKeys: string[], expire?: number): Promise<GetS3SignUrlsResponse>;

    /**
     * 获取全局权限控制
     */
    getGlobalControl(): Promise<GlobalControl>;

    /**
     * 埋点上报
     */
    biTrack(params: BiTrackParams): Promise<boolean>;

    /**
     * 初始化 Project
     */
    initProject(): Promise<unknown>;

    /**
     * 获取 Work Project 列表（/eureka/work/project/fetch-all）
     */
    getProjectList(): Promise<WorkProjectItem[]>;

    /**
     * 获取 TRIZ 原理列表
     */
    getTrizPrincipleList(keyword?: string): Promise<TrizPrincipleListResponse>;

    /**
     * 获取 TRIZ 原理详情
     */
    getTrizPrincipleDetail(principleId: string | number): Promise<TrizPrincipleDetailResponse>;

    /** 根据原理检索式生成标准检索预览 */
    previewTrizPrincipleSearch(principleQuery: string): Promise<TrizPrincipleSearchPreviewResponse>;

    uploadFileTechMind(files: File[]): Promise<PatSnapCommonResponseData<unknown>>;
}

export interface RdCard {
    function_id: string;
    type: string;
    agent_id?: number;
    name?: string;
    is_free?: boolean;
    [key: string]: unknown;
}

/**
 * Agentic Search API 服务标识符（用于依赖注入）
 */
export const AgenticSearchApi = Symbol('AgenticSearchApi');

/**
 * Agentic Search API 服务实现
 */
@injectable()
export class AgenticSearchApiImpl extends PsHttpService implements IAgenticSearchApi {
    get options() {
        return {
            baseURL: import.meta.env.VITE_EUREKA_SERVICE_URI,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    /**
     * 创建 TRIZ / DeepCost 任务
     */
    async createTrizJob(params: CreateTrizJobParams, agentType = FunctionId.TRIZ): Promise<{ job_id: string }> {
        const agentPath = agentType === FunctionId.DEEPCOST ? 'rd-deep-cost' : 'rd-triz-mind';
        return this.post<PatSnapCommonResponseData<{ job_id: string }>>({
            url: `/eureka-rd-agent/${agentPath}/init`,
            data: params,
            headers: getLlmModelHeader(agentType),
        }).then((res) => {
            return res.data as { job_id: string };
        });
    }

    /**
     * 创建 Tech Research Beta 任务(AI Agent 平台)
     */
    async createTechResearchBetaJob(params: createTechResearchBetaJobParams, agentType = FunctionId.DEEP_RESEARCH): Promise<{ job_id: string }> {
        return this.post<PatSnapCommonResponseData<{ job_id: string }>>({
            url: '/ai-agent-api/job/chat',
            data: { ...params, ...getLlmModelParam(agentType) },
            headers: getLlmModelHeader(agentType),
        }).then((res) => {
            return res as unknown as { job_id: string };
        });
    }

    /**
     * 创建 FMEA 任务
     */
    async createFmeaJob(params: CreateFmeaJobParams, agentType = FunctionId.FMEA): Promise<{ fmea_id: string }> {
        return this.post<PatSnapCommonResponseData<{ fmea_id: string }>>({
            url: '/eureka/fmea/create',
            data: params,
            headers: getLlmModelHeader(agentType),
        }).then((res) => {
            return res.data as { fmea_id: string };
        });
    }

    /**
     * 从上传的 Excel 创建 FMEA:multipart 直传后端 POI 解析,跳过意图/50字下限校验
     */
    async createFmeaFromExcel(file: File, content?: string, agentType = FunctionId.FMEA): Promise<{ fmea_id: string }> {
        const formData = new FormData();
        formData.append('file', file);
        if (content) {
            formData.append('content', content);
        }
        return this.post<PatSnapCommonResponseData<{ fmea_id: string }>>({
            url: '/eureka/fmea/create-from-excel',
            data: formData,
            headers: getLlmModelHeader(agentType),
        }).then((res) => {
            return res.data as { fmea_id: string };
        });
    }

    /**
     * 创建 Competitive Landscape 任务
     */
    async createCompetitiveLandscapeJob(params: CreateCompetitiveLandscapeJobParams, agentType = FunctionId.COMPETITIVE_LANDSCAPE): Promise<{ job_id: string }> {
        return this.post<PatSnapCommonResponseData<{ job_id: string }>>({
            url: '/eureka/competitive-landscape/create',
            data: params,
            headers: getLlmModelHeader(agentType),
        }).then((res) => {
            return res.data as { job_id: string };
        });
    }

    /**
     * 创建 Company Scout 任务（AI 对话式搜索）
     */
    async createCompanyScoutJob(params: CreateCompanyScoutJobParams, agentType = FunctionId.COMPANY_SEARCH): Promise<CreateCompanyScoutJobResponse> {
        return this.post<PatSnapCommonResponseData<CreateCompanyScoutJobResponse>>({
            url: '/eureka/company-scout/start',
            data: params,
            headers: getLlmModelHeader(agentType),
        }).then((res) => {
            return res.data as CreateCompanyScoutJobResponse;
        });
    }

    /**
     * 生成搜索查询 ID（传统搜索）
     */
    async generateSearchQueryId(params: GenerateSearchQueryIdRequest): Promise<GenerateSearchQueryIdResponse> {
        // 使用完整的 baseURL，因为这个 API 在 discovery 服务下
        const discoveryBaseUrl = import.meta.env.VITE_DISCOVERY_SERVICE_URI || '';
        return this.post<PatSnapCommonResponseData<GenerateSearchQueryIdResponse>>({
            url: `${discoveryBaseUrl}/discovery-api/2.0/search/history?source=homepage`,
            data: params,
        }).then((res) => {
            return res as unknown as GenerateSearchQueryIdResponse;
        });
    }

    /**
     * 获取公司搜索历史记录
     */
    async getCompanySearchHistory(params: GetCompanySearchHistoryRequest): Promise<GetCompanySearchHistoryResponse> {
        return this.post<PatSnapCommonResponseData<GetCompanySearchHistoryResponse>>({
            url: '/eureka/company-scout/history',
            data: params,
        }).then((res) => {
            return (res.data as GetCompanySearchHistoryResponse).slice(0, 3);
        });
    }

    /**
     * 获取智能联想建议（统一接口 /eureka/smart/suggest）
     * 返回数据包含多个 section（COMPANY、AUTOCOMPLETE 等）
     */
    async getSmartSuggestions(params: { keyword: string, fields: string }): Promise<AutoCompleteSuggestion[]> {
        return this.post<PatSnapCommonResponseData<AutoCompleteSuggestion[]>>({
            url: '/eureka/smart/suggest',
            data: params,
        }).then((res) => {
            return (res.data as AutoCompleteSuggestion[]).map(section => ({
                ...section,
                content: section.content.slice(0, 3),
            }));
        });
    }

    /**
     * 获取 RD 功能卡片列表
     */
    async getRdCards(): Promise<{ cards: RdCard[] }> {
        return this.get<PatSnapCommonResponseData<{ cards: RdCard[] }>>({
            url: '/eureka/rd-home-page/rd-cards',
        }).then((res) => {
            return res.data as { cards: RdCard[] };
        });
    }

    /**
     * Quick Chat 任务（AI Agent 平台）
     */
    async quickChatJob(params: { code: string; input: string }, agentType?: string): Promise<{ job_id: string }> {
        return this.post<PatSnapCommonResponseData<{ job_id: string }>>({
            url: '/ai-agent-api/job/quick-chat',
            data: { ...params, ...getLlmModelParam(agentType) },
            headers: getLlmModelHeader(agentType),
        }).then((res) => {
            return res as unknown as { job_id: string };
        });
    }

    /**
     * TRIZ / DeepCost 批量上传文件：FormData file_list[] 数组形式，支持图片与 Excel
     */
    async uploadFileBatch(files: File[], agentType = FunctionId.TRIZ): Promise<PatSnapCommonResponseData<unknown>> {
        const formData = new FormData();
        files.forEach((file) => formData.append('file_list', file));
        const agentPath = agentType === FunctionId.DEEPCOST ? 'rd-deep-cost' : 'rd-triz-mind';
        return this.request<PatSnapCommonResponseData<unknown>>({
            method: 'post',
            url: `/eureka-rd-agent/${agentPath}/upload-file/batch`,
            data: formData,
        }) as Promise<PatSnapCommonResponseData<unknown>>;
    }

    /**
     * TRIZ / DeepCost 删除已上传文件
     */
    async deleteUploadFileRecords(recordIds: string[], agentType = FunctionId.TRIZ): Promise<PatSnapCommonResponseData<unknown>> {
        const agentPath = agentType === FunctionId.DEEPCOST ? 'rd-deep-cost' : 'rd-triz-mind';
        return this.post<PatSnapCommonResponseData<unknown>>({
            url: `/eureka-rd-agent/${agentPath}/upload-file/delete`,
            data: { record_ids: recordIds },
        }) as Promise<PatSnapCommonResponseData<unknown>>;
    }

    /**
     * 获取 S3 签名 CDN 地址（用于图片预览等）
     */
    async getS3SignUrls(objectKeys: string[], expire = 21600): Promise<GetS3SignUrlsResponse> {
        const payload: GetS3SignUrlsRequest = { object_keys: objectKeys, expire };
        const res = await this.post<PatSnapCommonResponseData<GetS3SignUrlsResponse>>({
            url: '/eureka/s3/cdn/sign',
            data: payload,
        });
        return (res.data as GetS3SignUrlsResponse) ?? { resource: '', signed_urls: [] };
    }

    async getGlobalControl(): Promise<GlobalControl> {
        return this.get<PatSnapCommonResponseData<GlobalControl>>({
            url: '/eureka/amounts/control',
        }).then((res) => res.data as GlobalControl);
    }

    async biTrack(params: BiTrackParams): Promise<boolean> {
        return this.post<PatSnapCommonResponseData<boolean>>({
            url: '/eureka/tracking/add',
            data: params,
        }).then((res) => res.status as boolean);
    }

    async initProject(): Promise<unknown> {
        return this.get<PatSnapCommonResponseData<unknown>>({
            url: '/eureka/project/init',
        }).then((res) => res.data);
    }

    async getProjectList(): Promise<WorkProjectItem[]> {
        return this.get<PatSnapCommonResponseData<WorkProjectItem[]>>({
            url: '/eureka/work/project/fetch-all',
        }).then((res) => (res.data as WorkProjectItem[]) ?? []);
    }

    async getTrizPrincipleList(keyword?: string): Promise<TrizPrincipleListResponse> {
        return this.get<PatSnapCommonResponseData<TrizPrincipleListResponse>>({
            url: '/eureka-rd-agent/triz-principle/list',
            params: { keyword: keyword || '' },
        }).then((res) => res.data as TrizPrincipleListResponse);
    }

    async getTrizPrincipleDetail(principleId: string | number): Promise<TrizPrincipleDetailResponse> {
        return this.get<PatSnapCommonResponseData<TrizPrincipleDetailResponse>>({
            url: `/eureka-rd-agent/triz-principle/${principleId}`,
        }).then((res) => res.data as TrizPrincipleDetailResponse);
    }

    async previewTrizPrincipleSearch(principleQuery: string): Promise<TrizPrincipleSearchPreviewResponse> {
        return this.post<PatSnapCommonResponseData<TrizPrincipleSearchPreviewResponse>>({
            url: '/eureka/web-query/search-preview',
            headers: {
                ...this.options.headers,
                'X-PatSnap-From': 'w-eureka',
            },
            data: {
                playbook: 'SmartSearch',
                q_s: {
                    logic: 'AND',
                    type: 'BOOL',
                    items: [{
                        logic: 'AND',
                        type: 'COMMAND',
                        value: principleQuery,
                    }],
                },
            },
        }).then((res) => res.data as TrizPrincipleSearchPreviewResponse);
    }

    async uploadFileTechMind(files: File[]): Promise<PatSnapCommonResponseData<unknown>> {
        const formData = new FormData();
        files.forEach((file) => formData.append('file_list', file));
        return this.request<PatSnapCommonResponseData<unknown>>({
            method: 'post',
            url: '/eureka-rd-agent/tech-mind/upload',
            data: formData,
        }) as Promise<PatSnapCommonResponseData<unknown>>;
    }
}

/**
 * TechMind 初始化响应
 */
export interface InitTechMindResponse {
    job_id: string;
}

/**
 * TechMind 初始化参数
 */
export interface InitTechMindParams {
    input_text?: string;
    /** 关联的 work project id */
    work_project_id?: string;
    /** 已上传文件的 record_id 列表 */
    record_ids?: string[];
}

/**
 * TechMind API 服务接口
 */
export interface ITechMindApi {
    init(params?: InitTechMindParams, agentType?: string): Promise<InitTechMindResponse>;
}

export const TechMindApi = Symbol('TechMindApi');

@injectable()
export class TechMindApiImpl extends PsHttpService implements ITechMindApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/tech-mind`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    init(params?: InitTechMindParams, agentType = FunctionId.TECH_MIND): Promise<InitTechMindResponse> {
        return this.post<PatSnapCommonResponseData<InitTechMindResponse>>({
            url: '/init',
            data: { ...params, workflow_version: 'v2' },
            headers: getLlmModelHeader(agentType),
        }).then((res) => res.data as InitTechMindResponse);
    }
}
